import React from 'react';
import TasklistJsonPageHeader from '@/components/TasklistJsonPageHeader/TasklistJsonPageHeader';
import Button from '@/components/Button/Button';
import CommunityCard from '@/components/CommunityCard/CommunityCard';
import { CallIcon, ChatIcon, RedirectIcon } from '@/components/MenusIcons/MenusIcons';
import { getCommunityUrl, openChat } from '@/utils/linksUtils';
import { useOverviewContext } from '@/context/OverviewContext';
import { useToolSettings } from '@/context/ToolSettingsContext';
import { getActiveToolIDs, shouldShowLearningCoachCommunity } from '@/utils/toolSettingsUtils';
import { useSchool } from '@/context/SchoolContext';
import cardMediaCommunity from '@/assets/images/k12_community_img_square.svg';

const CONTACT_CATEGORIES = [
  {
    title: 'Advisors / Homeroom Teachers',
    items: [
      'Make-Up Work',
      'Attendance support',
      'Temporary schedule changes',
      'Workload support',
    ],
  },
  {
    title: 'Registrar',
    items: [
      'Address Changes',
      'Custody Changes',
      'School Records',
      'Moves to another state',
    ],
  },
  {
    title: 'Counselor or Social Worker',
    items: [
      'Family crisis support',
      'Mental Health concerns',
      'Unsafe situations',
      'Community Resources',
    ],
  },
  {
    title: 'School Leadership',
    items: ['Urgent issues impacting schooling', 'Escalation support'],
  },
  {
    title: 'Tech Support',
    items: ['Device issues', 'Login/Access problems', 'Live class tech problems'],
  },
] as const;

interface SupportSectionProps {
  eyebrow: string;
  title: string;
  description: string;
  className?: string;
  children: React.ReactNode;
}

const SupportSection: React.FC<SupportSectionProps> = ({
  eyebrow,
  title,
  description,
  className = '',
  children,
}) => (
  <section className={`support-page__section ${className}`.trim()}>
    <div className="support-page__section-header">
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
    <div className="support-page__section-body">{children}</div>
  </section>
);

interface ContactCategoryCardProps {
  title: string;
  items: readonly string[];
}

const ContactCategoryCard: React.FC<ContactCategoryCardProps> = ({ title, items }) => (
  <div className="support-page__contact-card">
    <h3>{title}</h3>
    <ul>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </div>
);

interface SupportColumnProps {
  title: string;
  children: React.ReactNode;
  actions: React.ReactNode;
}

const SupportColumn: React.FC<SupportColumnProps> = ({ title, children, actions }) => (
  <div className="support-page__support-column">
    <h3>{title}</h3>
    <p>{children}</p>
    <div className="support-page__support-column-actions">{actions}</div>
  </div>
);

const Support = () => {
  const { userRoleVal, loggedInUser = null, identityId } = useOverviewContext();
  const { toolSettings } = useToolSettings();
  const { data: schoolData } = useSchool();
  const activeToolIDs = getActiveToolIDs(toolSettings);
  const showLearningCoachCommunity = shouldShowLearningCoachCommunity(userRoleVal, activeToolIDs);
  const communityLink = getCommunityUrl();
  const schoolAbbr = schoolData?.abbreviation ?? 'your school';

  return (
    <div className="support-page tasklist-json-page tasklist-json-page--tasklist">
      <TasklistJsonPageHeader
        variant="tasklist"
        learningCenterHubLanding
        title="Help & Support"
        description="Call, email, or chat online when you have questions. We're here to help!"
      />

      <div className="tasklist-json-body">
        <div className="tasklist-json-post-layout support-page__content">
          <SupportSection
            eyebrow="School Support"
            title={`Who to Contact at ${schoolAbbr}`}
            description="Reach out to the right person on your school team for school-specific questions."
            className="support-page__section--school"
          >
            <div className="support-page__contact-grid">
              {CONTACT_CATEGORIES.map((category) => (
                <ContactCategoryCard
                  key={category.title}
                  title={category.title}
                  items={category.items}
                />
              ))}
            </div>
          </SupportSection>

          <SupportSection
            eyebrow="K12 Support"
            title="Additional Support from K12"
            description="Self-serve resources and live support for K12-wide questions like account access, technology, and shipping."
          >
            <div className="support-page__k12-columns">
              <SupportColumn
                title="Find lots of answers here!"
                actions={
                  <Button
                    label="Learn more"
                    icon={<RedirectIcon />}
                    iconPosition="after"
                    color="primary"
                    breakpoint="all"
                    linkUrl="https://help.k12.com"
                    linkTarget="_blank"
                    className="support-page__action-btn"
                  />
                }
              >
                The{' '}
                <a href="https://help.k12.com" target="_blank" rel="noopener noreferrer">
                  Help.K12.com
                </a>{' '}
                resource hub connects you with helpful information, practical guides, and instructions
                for all kinds of support — from troubleshooting computer issues to tutorials to return
                shipping labels. When you need help, start here!
              </SupportColumn>

              <SupportColumn
                title="Get Live Support"
                actions={
                  <>
                    <Button
                      label="Call"
                      icon={<CallIcon />}
                      iconPosition="before"
                      color="primary"
                      breakpoint="all"
                      className="support-page__action-btn"
                      linkUrl="tel:18669109528"
                    />
                    <Button
                      label="Start a chat"
                      icon={<ChatIcon />}
                      iconPosition="before"
                      color="primary"
                      breakpoint="all"
                      className="support-page__action-btn"
                      onClick={() => openChat(identityId)}
                    />
                  </>
                }
              >
                Talk to a support rep for help with any questions not specifically addressed on our
                help site. You can start a chat or call{' '}
                <strong>866.910.9528</strong> to contact a customer support representative.
              </SupportColumn>
            </div>

            {loggedInUser?.role === 'LEARNING_COACH' && showLearningCoachCommunity && (
              <CommunityCard
                type="learningCoach"
                linkUrl={communityLink}
                orientation="horizontal"
                descriptionOverride="Want a real answer from someone who's been there? Consult the Learning Coach Community where valued members of this secure, caring network share advice and offer support."
                buttonTextOverride="Find your Community"
                mediaOverride={cardMediaCommunity}
                hideEyebrow
              />
            )}
          </SupportSection>
        </div>
      </div>
    </div>
  );
};

export default Support;
